import React, { useEffect, useMemo, useState } from 'react';

import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGridPagination } from 'components/atoms/DataGridPagination';
import { getRenderSlideIdCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { compact, filter, includes, map, sortBy, uniqBy } from 'es-toolkit/compat';
import { Annotation, AnnotationAssignment, AnnotationWorkingStates } from 'interfaces/annotation';
import { Permission } from 'interfaces/permissionOption';
import { AnnotationWorkingStatesIcons } from 'services/annotations/workingStates';
import { useCurrentUser } from 'utils/useCurrentUser';
import { usePermissions } from 'utils/usePermissions';

import {
  ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS,
  AnnotationAssignmentDisplayStatus,
  filterAnnotationRows,
  getAnnotationDisplayStatus,
  getAnnotationWorkingStateSortValue,
  getTotalAnnotationsCount,
  NOT_ASSIGNED_DISPLAY_STATUS,
} from './AnnotationAssignmentTable.utils';
import AnnotationAssignmentToolbar, { AnnotationAssignmentToolbarProps } from './AnnotationAssignmentTableToolbar';
import { useBulkAnnotationWorkingState } from './useBulkAnnotationWorkingState';

interface AnnotationAssignmentTableProps {
  studyId: string;
  annotationAssignment: AnnotationAssignment;
}

const ANNOTATIONS_PAGE_SIZE = 25;
const ANNOTATIONS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const ANNOTATIONS_TABLE_HEIGHT = 620;

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends AnnotationAssignmentToolbarProps {}
}

const AnnotationAssignmentTable: React.FC<AnnotationAssignmentTableProps> = ({ studyId, annotationAssignment }) => {
  const theme = useTheme();
  const currentUser = useCurrentUser();
  const { hasPermission } = usePermissions();
  const canAnnotateSlides = hasPermission(Permission.AnnotateSlides);

  const [searchText, setSearchText] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [selectedTaggerIds, setSelectedTaggerIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<AnnotationAssignmentDisplayStatus[]>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: ANNOTATIONS_PAGE_SIZE,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const rows = annotationAssignment?.annotations ?? [];
  const assignmentStudyId = annotationAssignment?.studyId ?? studyId;

  const availableTaggers = useMemo(() => sortBy(uniqBy(compact(map(rows, 'tagger')), 'taggerId'), 'name'), [rows]);
  const selectedRows = useMemo(
    () => filter(rows, ({ annotationId }) => includes(rowSelectionModel, annotationId)),
    [rowSelectionModel, rows]
  );
  const handleCompletedBulkAction = React.useCallback((completedAnnotationIds: number[]) => {
    setRowSelectionModel((selectedIds) =>
      filter(selectedIds, (selectedId) => !includes(completedAnnotationIds, selectedId))
    );
  }, []);
  const {
    submit: submitBulkWorkingState,
    isPending: isBulkActionPending,
    pendingTarget,
  } = useBulkAnnotationWorkingState({
    annotationAssignmentId: annotationAssignment.annotationAssignmentId,
    selectedRows,
    onCompleted: handleCompletedBulkAction,
  });

  const filteredRows = useMemo(
    () =>
      filterAnnotationRows({
        rows,
        searchText,
        mineOnly,
        selectedTaggerIds,
        selectedStatuses,
        currentUserId: currentUser?.userId,
      }),
    [currentUser?.userId, mineOnly, rows, searchText, selectedStatuses, selectedTaggerIds]
  );

  const hasActiveFilters =
    Boolean(searchText) || mineOnly || selectedTaggerIds.length > 0 || selectedStatuses.length > 0;

  useEffect(() => {
    setPaginationModel((currentPaginationModel) => ({ ...currentPaginationModel, page: 0 }));
  }, [mineOnly, searchText, selectedStatuses, selectedTaggerIds]);

  const dataGridSx = useMemo(
    () => ({
      width: '100%',
      overflowX: 'auto',
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[900],
        borderRadius: 0,
        borderTop: `1px solid ${theme.palette.mode === 'light' ? 'lightgrey' : 'rgb(81, 81, 81)'}`,
      },
      '& .MuiDataGrid-footerContainer': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
      '.annotating': {
        color: theme.palette.primary.main,
      },
      '.not-assigned': {
        color: theme.palette.text.secondary,
      },
      '.qa': {
        color: theme.palette.info.main,
      },
      '.done': {
        color: theme.palette.success.main,
      },
      backgroundColor: theme.palette.background.paper,
    }),
    [theme]
  );

  const columns: GridColDef<Annotation>[] = [
    {
      field: 'slideId',
      headerName: 'Slide ID',
      width: 350,
      renderCell: getRenderSlideIdCell({
        target: '_blank',
        rel: 'noopener noreferrer',
        caseStudyId: assignmentStudyId,
        activeAnnotationAssignment: annotationAssignment?.annotationAssignmentId,
      }),
    },
    {
      field: 'savedAnnotations',
      headerName: 'Saved annotations',
      width: 180,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_value, annotation) => getTotalAnnotationsCount(annotation.annotationSummary),
    },
    {
      field: 'draftAnnotations',
      headerName: 'Draft annotations',
      width: 180,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_value, annotation) => getTotalAnnotationsCount(annotation.draftAnnotationSummary),
    },
    {
      field: 'tagger',
      headerName: 'Assignee',
      width: 220,
      valueGetter: (_value, annotation) => annotation.tagger?.name || 'Unassigned',
    },
    {
      field: 'displayStatus',
      headerName: 'Status',
      width: 200,
      valueGetter: (_value, annotation) => getAnnotationDisplayStatus(annotation),
      sortComparator: (leftState, rightState) =>
        getAnnotationWorkingStateSortValue(leftState as AnnotationAssignmentDisplayStatus) -
        getAnnotationWorkingStateSortValue(rightState as AnnotationAssignmentDisplayStatus),
      renderCell: renderStatusCell,
    },
  ];

  const autoHeight = filteredRows.length <= paginationModel.pageSize;

  return (
    <Box
      sx={{
        width: '100%',
        height: autoHeight ? undefined : ANNOTATIONS_TABLE_HEIGHT,
        display: 'flex',
      }}
    >
      <DataGrid
        slots={{
          toolbar: AnnotationAssignmentToolbar,
          pagination: DataGridPagination,
        }}
        slotProps={{
          toolbar: {
            annotationAssignment,
            searchText,
            onSearchTextChange: setSearchText,
            mineOnly,
            onMineOnlyChange: setMineOnly,
            selectedTaggerIds,
            onSelectedTaggerIdsChange: setSelectedTaggerIds,
            selectedStatuses,
            onSelectedStatusesChange: setSelectedStatuses,
            availableTaggers,
            totalRowsCount: rows.length,
            filteredRowsCount: filteredRows.length,
            hasActiveFilters,
            selectedRows,
            canAnnotateSlides,
            isBulkActionPending,
            pendingTarget,
            onBulkWorkingStateChange: submitBulkWorkingState,
            onClearFilters: () => {
              setSearchText('');
              setMineOnly(false);
              setSelectedTaggerIds([]);
              setSelectedStatuses([]);
            },
          },
          pagination: {
            SelectProps: {
              sx: { width: 'auto' },
            },
          },
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'displayStatus', sort: 'asc' }],
          },
        }}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={ANNOTATIONS_PAGE_SIZE_OPTIONS}
        getCellClassName={getCellClassName}
        rows={filteredRows}
        columns={columns}
        getRowId={getRowId}
        sx={dataGridSx}
        density="compact"
        checkboxSelection={canAnnotateSlides}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(nextRowSelectionModel: GridRowId[]) => setRowSelectionModel(nextRowSelectionModel)}
        isRowSelectable={({ row }) => getAnnotationDisplayStatus(row as Annotation) !== NOT_ASSIGNED_DISPLAY_STATUS}
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
      />
    </Box>
  );
};

function getRowId(row: Annotation) {
  return row.annotationId;
}

function getCellClassName(params: { field: string; row: Annotation }) {
  if (params.field === 'displayStatus') {
    const state = getAnnotationDisplayStatus(params.row);
    return state === AnnotationWorkingStates.Annotating
      ? 'annotating'
      : state === NOT_ASSIGNED_DISPLAY_STATUS
      ? 'not-assigned'
      : state === AnnotationWorkingStates.QA
      ? 'qa'
      : 'done';
  }

  return '';
}

const renderStatusCell: GridColDef['renderCell'] = (params) => {
  const state = getAnnotationDisplayStatus(params.row as Annotation);
  return (
    <Grid container direction="row" alignItems="center" height="100%">
      <Grid item display="flex">
        {state === NOT_ASSIGNED_DISPLAY_STATUS ? (
          <PersonOffRoundedIcon />
        ) : (
          AnnotationWorkingStatesIcons[state as AnnotationWorkingStates] || <DescriptionRoundedIcon />
        )}
      </Grid>
      <Grid item>
        <Typography sx={{ ml: 1 }}>{ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS[state]}</Typography>
      </Grid>
    </Grid>
  );
};

export default AnnotationAssignmentTable;
